export type ConnectionQuality = 'excellent' | 'good' | 'fair' | 'poor' | 'unknown';

export interface ConnectionStats {
  quality: ConnectionQuality;
  bytesReceived: number;
  bytesSent: number;
  packetsLost: number;
  packetsReceived: number;
  roundTripTime: number;
  jitter: number;
  bitrate: number;
}

export class ConnectionQualityService {
  private previousStats: {
    timestamp: number;
    bytesReceived: number;
    bytesSent: number;
  } | null = null;

  /**
   * Get connection quality metrics from WebRTC stats
   */
  async getConnectionStats(
    peerConnection: RTCPeerConnection,
  ): Promise<ConnectionStats> {
    const stats = await peerConnection.getStats();

    let bytesReceived = 0;
    let bytesSent = 0;
    let packetsLost = 0;
    let packetsReceived = 0;
    let roundTripTime = 0;
    let jitter = 0;

    stats.forEach((report) => {
      // Inbound RTP (receiving video/audio)
      if (report.type === 'inbound-rtp' && report.mediaType === 'video') {
        bytesReceived = report.bytesReceived || 0;
        packetsLost = report.packetsLost || 0;
        packetsReceived = report.packetsReceived || 0;
        jitter = report.jitter || 0;
      }

      // Outbound RTP (sending video/audio)
      if (report.type === 'outbound-rtp' && report.mediaType === 'video') {
        bytesSent = report.bytesSent || 0;
      }

      // Candidate pair (RTT info)
      if (report.type === 'candidate-pair' && report.state === 'succeeded') {
        roundTripTime = report.currentRoundTripTime || 0;
      }
    });

    // Calculate bitrate
    const bitrate = this.calculateBitrate(bytesReceived);

    // Determine quality
    const quality = this.calculateQuality(
      bitrate,
      packetsLost,
      packetsReceived,
      roundTripTime,
      jitter,
    );

    return {
      quality,
      bytesReceived,
      bytesSent,
      packetsLost,
      packetsReceived,
      roundTripTime: Math.round(roundTripTime * 1000), // Convert to ms
      jitter: Math.round(jitter * 1000), // Convert to ms
      bitrate: Math.round(bitrate / 1000), // Convert to kbps
    };
  }

  /**
   * Calculate bitrate from bytes received
   */
  private calculateBitrate(bytesReceived: number): number {
    const now = Date.now();

    if (this.previousStats) {
      const timeDiff = (now - this.previousStats.timestamp) / 1000; // seconds
      const bytesDiff = bytesReceived - this.previousStats.bytesReceived;

      if (timeDiff > 0) {
        const bitrate = (bytesDiff * 8) / timeDiff; // bits per second

        // Update previous stats
        this.previousStats = {
          timestamp: now,
          bytesReceived,
          bytesSent: this.previousStats.bytesSent,
        };

        return bitrate;
      }
    }

    // Initialize previous stats
    this.previousStats = {
      timestamp: now,
      bytesReceived,
      bytesSent: 0,
    };

    return 0;
  }

  /**
   * Calculate overall connection quality
   */
  private calculateQuality(
    bitrate: number,
    packetsLost: number,
    packetsReceived: number,
    rtt: number,
    jitter: number,
  ): ConnectionQuality {
    const totalPackets = packetsLost + packetsReceived;
    const packetLossPercentage =
      totalPackets > 0 ? (packetsLost / totalPackets) * 100 : 0;

    // Quality scoring
    let score = 100;

    // Bitrate scoring (lower bitrate = lower score)
    // Good: > 1 Mbps, Fair: 500kbps - 1Mbps, Poor: < 500kbps
    if (bitrate < 500000) {
      // < 500 kbps
      score -= 40;
    } else if (bitrate < 1000000) {
      // 500kbps - 1Mbps
      score -= 20;
    }

    // Packet loss scoring
    // Good: < 1%, Fair: 1-3%, Poor: > 3%
    if (packetLossPercentage > 3) {
      score -= 30;
    } else if (packetLossPercentage > 1) {
      score -= 15;
    }

    // RTT scoring (latency)
    // Good: < 100ms, Fair: 100-300ms, Poor: > 300ms
    const rttMs = rtt * 1000;
    if (rttMs > 300) {
      score -= 20;
    } else if (rttMs > 100) {
      score -= 10;
    }

    // Jitter scoring
    // Good: < 30ms, Fair: 30-50ms, Poor: > 50ms
    const jitterMs = jitter * 1000;
    if (jitterMs > 50) {
      score -= 10;
    } else if (jitterMs > 30) {
      score -= 5;
    }

    // Determine quality from score
    if (score >= 80) return 'excellent';
    if (score >= 60) return 'good';
    if (score >= 40) return 'fair';
    return 'poor';
  }

  /**
   * Get quality display info for UI
   */
  getQualityInfo(quality: ConnectionQuality): {
    quality: ConnectionQuality;
    label: string;
    color: string;
    icon: string;
  } {
    switch (quality) {
      case 'excellent':
        return {
          quality: 'excellent',
          label: 'Excellent',
          color: '#22c55e',
          icon: '📶',
        };
      case 'good':
        return {
          quality: 'good',
          label: 'Good',
          color: '#84cc16',
          icon: '📶',
        };
      case 'fair':
        return {
          quality: 'fair',
          label: 'Fair',
          color: '#f59e0b',
          icon: '📡',
        };
      case 'poor':
        return {
          quality: 'poor',
          label: 'Poor',
          color: '#ef4444',
          icon: '📡',
        };
      default:
        return {
          quality: 'unknown',
          label: 'Unknown',
          color: '#6b7280',
          icon: '❓',
        };
    }
  }

  /**
   * Reset stats (call when starting new connection)
   */
  reset(): void {
    this.previousStats = null;
  }
}

export default ConnectionQualityService;
